'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from './NeumoProductExplorer.module.scss';
import {
  IconCategory,
  IconSearch,
  IconSortDescending,
  IconSparkles,
  IconX,
} from '@tabler/icons-react';
import { Input, Pagination } from '@mantine/core';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { fetchProducts } from '@/src/helpers/fetch-products';
import type { Product } from '@/src/types/Header';

type Props = {
  lang: 'fr' | 'en';
};

type ViewMode = 'filter' | 'sections';
type SortMode = 'newest' | 'az';

function getLocalizedText(val: any, lang: 'fr' | 'en') {
  if (!val) {
    return '';
  }
  if (typeof val === 'string') {
    return val;
  }
  return val?.[lang] ?? val?.fr ?? val?.en ?? '';
}

function normalizeCategory(cat?: string) {
  const c = (cat || '').trim();
  return c || 'default';
}

function prettifyCategory(cat: string, pageCopy: any) {
  if (cat === 'default') {
    return pageCopy.defaultCategoryLabel;
  } // ✅ translated from dictionary
  return cat.replaceAll('_', ' ').replaceAll('-', ' ');
}

export default function NeumoProductsExplorer({ lang }: Props) {
  const dictionary = useDictionary();
  const pageCopy = dictionary.pages.products;

  // data
  const [products, setProducts] = useState<Product[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // UI
  const [viewMode, setViewMode] = useState<ViewMode>('filter');
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const realizationsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const run = async () => {
      const res: any = await fetchProducts(page, searchQuery);
      setProducts(res.products ?? []);
      setTotalPages(res.totalPages ?? 1);
    };
    run();
  }, [page, searchQuery]);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(products.map((p) => normalizeCategory((p as any).category))));
    return cats.sort((a, b) =>
      prettifyCategory(a, pageCopy).localeCompare(prettifyCategory(b, pageCopy), lang)
    );
  }, [products, pageCopy, lang]);

  useEffect(() => {
    if (selectedCategory !== 'all' && categories.length && !categories.includes(selectedCategory)) {
      setSelectedCategory('all');
    }
  }, [categories, selectedCategory]);

  const sortedProducts = useMemo(() => {
    const list = [...products];

    if (sortMode === 'newest') {
      list.sort((a: any, b: any) => {
        const da = new Date(a.createdAt || 0).getTime();
        const db = new Date(b.createdAt || 0).getTime();
        return db - da;
      });
    } else {
      list.sort((a: any, b: any) => {
        const ta = getLocalizedText(a.title, lang).toLowerCase();
        const tb = getLocalizedText(b.title, lang).toLowerCase();
        return ta.localeCompare(tb, lang);
      });
    }

    return list;
  }, [products, sortMode, lang]);

  const filteredProducts = useMemo(() => {
    return selectedCategory === 'all'
      ? sortedProducts
      : sortedProducts.filter((p: any) => normalizeCategory(p.category) === selectedCategory);
  }, [sortedProducts, selectedCategory]);

  const groupedByCategory = useMemo(() => {
    const map = new Map<string, Product[]>();
    for (const p of sortedProducts) {
      const key = normalizeCategory((p as any).category);
      map.set(key, [...(map.get(key) || []), p]);
    }
    return Array.from(map.entries()).sort(([a], [b]) =>
      prettifyCategory(a, pageCopy).localeCompare(prettifyCategory(b, pageCopy), lang)
    );
  }, [sortedProducts, pageCopy, lang]);

  const resultsCount = viewMode === 'filter' ? filteredProducts.length : sortedProducts.length;

  const clearSearch = () => {
    setSearchQuery('');
    setPage(1);
  };

  return (
    <section className={styles.hero} aria-labelledby="products-title">
      <div className={styles.container}>
        <div className={styles.aiHeadVisual} aria-hidden="true">
          <div className={styles.aiHeadGlow} />
        </div>

        <div className={styles.surface}>
          <div className={styles.surfaceContainer}>
            {/* HEADER */}
            <header className={styles.header}>
              <p className={styles.eyebrow}>{pageCopy.eyebrow}</p>

              <h1 id="products-title" className={styles.title}>
                {pageCopy.title}
              </h1>

              <p className={styles.subtitle}>{pageCopy.search}</p>

              {pageCopy.seoIntro ? (
                <div className={styles.seoIntro}>
                  <p>{pageCopy.seoIntro}</p>
                </div>
              ) : null}
            </header>

            {/* ======= OFFERS (FIXES) ======= */}
            <section className={styles.offers} aria-label={pageCopy.offersAriaLabel}>
              <div className={styles.blockHeader}>
                <h2 className={styles.blockTitle}>{pageCopy.offersTitle}</h2>
                <p className={styles.blockSubtitle}>{pageCopy.offersSubtitle}</p>
              </div>

              <div
                className={styles.offersGrid}
                role="list"
                aria-label={pageCopy.offersListAriaLabel}
              >
                <OfferCard
                  title={pageCopy.offer1Title}
                  price={pageCopy.offer1Price}
                  items={[pageCopy.offer1Item1, pageCopy.offer1Item2, pageCopy.offer1Item3]}
                  lang={lang}
                  ctaLabel={pageCopy.offerCtaLabel}
                />
                <OfferCard
                  title={pageCopy.offer2Title}
                  price={pageCopy.offer2Price}
                  items={[pageCopy.offer2Item1, pageCopy.offer2Item2, pageCopy.offer2Item3]}
                  lang={lang}
                  ctaLabel={pageCopy.offerCtaLabel}
                  featured
                />
                <OfferCard
                  title={pageCopy.offer3Title}
                  price={pageCopy.offer3Price}
                  items={[pageCopy.offer3Item1, pageCopy.offer3Item2, pageCopy.offer3Item3]}
                  lang={lang}
                  ctaLabel={pageCopy.offerCtaLabel}
                />
              </div>

              {/* Deal */}
              <div className={styles.deal} aria-label={pageCopy.dealAriaLabel}>
                <div className={styles.dealLeft}>
                  <div className={styles.dealBadge}>
                    <IconSparkles size={16} />
                    <span>{pageCopy.dealBadge}</span>
                  </div>
                  <h3 className={styles.dealTitle}>{pageCopy.dealTitle}</h3>
                  <p className={styles.dealText}>{pageCopy.dealText}</p>
                </div>

                <div className={styles.dealRight}>
                  <button
                    type="button"
                    className={styles.dealCta}
                    onClick={() => (window.location.href = `/${lang}/contact`)}
                  >
                    {pageCopy.dealCtaLabel}
                  </button>
                </div>
              </div>
            </section>

            {/* ======= REALIZATIONS (PAGINATED) ======= */}
            <div ref={realizationsRef} />

            <section className={styles.realizations} aria-label={pageCopy.realizationsAriaLabel}>
              <div className={styles.blockHeader}>
                <h2 className={styles.blockTitle}>{pageCopy.realizationsTitle}</h2>
                <p className={styles.blockSubtitle}>{pageCopy.realizationsSubtitle}</p>
              </div>

              {/* OPTIONS TOP */}
              <div className={styles.options}>
                <div className={styles.optionsLeft}>
                  <div className={styles.optionsLabel}>
                    <IconCategory size={16} />
                    <span>{pageCopy.categoryTitle}</span>
                  </div>

                  <div
                    className={styles.chipsRow}
                    role="list"
                    aria-label={pageCopy.categoriesAriaLabel}
                  >
                    <button
                      type="button"
                      className={`${styles.chip} ${selectedCategory === 'all' ? styles.chipActive : ''}`}
                      onClick={() => {
                        setSelectedCategory('all');
                        setPage(1);
                      }}
                    >
                      {pageCopy.allLabel}
                    </button>

                    {categories.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        className={`${styles.chip} ${selectedCategory === cat ? styles.chipActive : ''}`}
                        onClick={() => {
                          setSelectedCategory(cat);
                          setPage(1);
                          setViewMode('filter');
                        }}
                      >
                        {prettifyCategory(cat, pageCopy)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={styles.optionsRight}>
                  <div className={styles.pills}>
                    <button
                      type="button"
                      className={styles.pill}
                      onClick={() => setSortMode((s) => (s === 'newest' ? 'az' : 'newest'))}
                      aria-label={pageCopy.sortToggleAriaLabel}
                      title={pageCopy.sortToggleTitle}
                    >
                      <IconSortDescending size={16} />
                      <span>
                        {sortMode === 'newest' ? pageCopy.sortNewestLabel : pageCopy.sortAzLabel}
                      </span>
                    </button>
                  </div>

                  <div className={styles.resultsInfo}>
                    <span className={styles.resultsNumber}>{resultsCount}</span>
                    <span>{pageCopy.resultsLabel}</span>
                    {selectedCategory !== 'all' && viewMode === 'filter' ? (
                      <span className={styles.resultsMeta}>
                        · {prettifyCategory(selectedCategory, pageCopy)}
                      </span>
                    ) : null}
                    {searchQuery ? (
                      <span className={styles.resultsMeta}>· “{searchQuery}”</span>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* SEARCH */}
              <div className={styles.searchRow}>
                <Input
                  placeholder={pageCopy.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  style={{ width: '100%' }}
                  leftSection={<IconSearch size={18} />}
                  classNames={{ input: styles.searchInput }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className={styles.clearBtn}
                    onClick={clearSearch}
                    aria-label={pageCopy.clearSearchAriaLabel}
                    title={pageCopy.clearSearchTitle}
                  >
                    <IconX size={18} />
                  </button>
                )}
              </div>

              {/* CONTENT */}
              <div className={styles.content}>
                {viewMode === 'filter' ? (
                  <>
                    <div
                      className={styles.grid}
                      role="list"
                      aria-label={pageCopy.realizationsListAriaLabel}
                    >
                      {filteredProducts.map((p: any) => (
                        <ProductCard
                          key={p._id ?? getLocalizedText(p.title, lang)}
                          product={p}
                          lang={lang}
                          pageCopy={pageCopy}
                        />
                      ))}
                    </div>

                    <div className={styles.paginationRow}>
                      <Pagination
                        value={page}
                        onChange={setPage}
                        total={totalPages}
                        radius="xl"
                        color="dark"
                        aria-label="Pagination"
                        getItemProps={(p) => ({
                          'aria-label': `Go to page ${p}`,
                        })}
                        getControlProps={(control) => ({
                          'aria-label':
                            control === 'next'
                              ? 'Next page'
                              : control === 'previous'
                                ? 'Previous page'
                                : control === 'first'
                                  ? 'First page'
                                  : 'Last page',
                        })}
                      />
                    </div>
                  </>
                ) : (
                  <div className={styles.sections}>
                    {groupedByCategory.map(([cat, items]) => (
                      <section
                        key={cat}
                        className={styles.section}
                        aria-label={prettifyCategory(cat, pageCopy)}
                      >
                        <div className={styles.sectionHeader}>
                          <h3 className={styles.sectionTitle}>{prettifyCategory(cat, pageCopy)}</h3>
                          <p className={styles.sectionHint}>{pageCopy.sectionHint}</p>
                        </div>

                        <div
                          className={styles.grid}
                          role="list"
                          aria-label={pageCopy.realizationsListAriaLabel}
                        >
                          {items.map((p: any) => (
                            <ProductCard
                              key={p._id ?? getLocalizedText(p.title, lang)}
                              product={p}
                              lang={lang}
                              pageCopy={pageCopy}
                            />
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Bottom SEO */}
            <footer className={styles.footerSeo}>
              <p>{pageCopy.footerSeoText}</p>
              <Link href={`/${lang}/contact`} className={styles.footerLink}>
                {pageCopy.footerCtaLabel}
              </Link>
            </footer>
          </div>
        </div>
      </div>
    </section>
  );
}

function OfferCard({
  title,
  items,
  featured,
  lang,
  ctaLabel,
}: {
  title: string;
  price: string;
  items: string[];
  featured?: boolean;
  lang: 'fr' | 'en';
  ctaLabel: string;
}) {
  return (
    <article
      className={`${styles.offerCard} ${featured ? styles.offerCardFeatured : ''}`}
      role="listitem"
    >
      <div className={styles.offerTop}>
        <h3 className={styles.offerTitle}>{title}</h3>
      </div>

      <ul className={styles.offerList}>
        {items.map((it, idx) => (
          <li key={idx}>{it}</li>
        ))}
      </ul>

      <Link href={`/${lang}/contact`} className={styles.offerCta}>
        {ctaLabel}
      </Link>
    </article>
  );
}

function ProductCard({
  product,
  lang,
  pageCopy,
}: {
  product: any;
  lang: 'fr' | 'en';
  pageCopy: any;
}) {
  const title = getLocalizedText(product?.title, lang);
  const description = getLocalizedText(product?.description, lang);

  const rawTitle = (title || pageCopy.fallbackSlugLabel).trim();
  const href = `/${lang}/products/${encodeURIComponent(rawTitle)}`;

  const category = prettifyCategory(normalizeCategory(product?.category), pageCopy);
  const hasPrice = product?.price !== null && product?.price !== undefined && product?.price !== '';

  return (
    <article className={styles.card} role="listitem">
      <Link href={href} className={styles.cardLink} aria-label={title}>
        <div className={styles.media}>
          <Image
            src={product?.mainImage ?? ''}
            alt={title}
            fill
            sizes="(max-width: 768px) 100vw, 33vw"
            style={{ objectFit: 'cover' }}
          />
          <div className={styles.mediaOverlay} aria-hidden="true" />
          <div className={styles.badge}>{category}</div>
        </div>

        <div className={styles.cardBody}>
          <h3 className={styles.cardTitle}>{title}</h3>
          <p className={styles.cardDesc}>{description}</p>

          <div className={styles.cardBottom}>
            <div className={styles.priceBlock}>
              {hasPrice ? (
                <>
                  <div className={styles.price}>
                    {product.price} {pageCopy.currencySymbol}
                  </div>
                  <div className={styles.priceHint}>{pageCopy.priceHintLabel}</div>
                </>
              ) : (
                <>
                  <div className={styles.priceAlt}>{pageCopy.priceOnRequestLabel}</div>
                  <div className={styles.priceHint}>{pageCopy.contactUsLabel}</div>
                </>
              )}
            </div>

            <span className={styles.cta}>{pageCopy.cardCtaLabel}</span>
          </div>
        </div>
      </Link>
    </article>
  );
}
